import EnhancementManager from 'reader-enhancements';
import FindApi from './findApi';
import PdfPrint from './pdfPrint';
import { CSS_UNITS } from '../../constants';
import pdfDidRender from '../pageLoadUtils';
import setupIntegrations from './integrations/setupIntegrations';
import { PdfViewerFactory } from './pdfViewerFactories';
import { identificationType, referenceType } from '../../../shared/constants';
import { to4Decimal } from '../../../shared/actions/setScale';

const { doi, pii } = identificationType;

const { bibliography, figure } = referenceType;

const {
  config: { feature },
  reduxState: { idType },
} = window;

const processors = (function createProcessorNames() {
  switch (idType) {
    case pii:
      return ['references', 'authors'];
    case doi:
      return ['authors'];
    default:
      return [];
  }
}());

const referencesOptions = {
  referenceTypes: feature.figureViewer
    ? [bibliography, figure]
    : [bibliography],
};

const authorsOptions = {
  onlyMatchBySurname: idType === doi,
};

export class PdfViewerApplication {
  constructor() {
    this.pdfViewer = null;
    this.pdfLinkService = null;
    this.findApi = new FindApi();
    this.enhancementManager = new EnhancementManager({
      processors,
      authorsOptions,
      referencesOptions,
    });
    this.pdfPrint = new PdfPrint();
  }

  init(
    containerElement,
    viewerElement,
    onUpdateFindControlState,
    onPageChange,
    dispatchEnhancementEvent,
    dispatchPrintEvent,
  ) {
    const pdfViewerFactoryModel = {
      container: containerElement,
      viewer: viewerElement,
    };

    const {
      eventBus,
      pdfLinkService,
      pdfFindController,
      pdfViewer,
    } = new PdfViewerFactory(pdfViewerFactoryModel);

    this.pdfLinkService = pdfLinkService;
    this.pdfViewer = pdfViewer;

    this.pdfViewer.eventBus.on('pagechanging', onPageChange);
    this.pdfViewer.eventBus.on('updatefindcontrolstate', onUpdateFindControlState);

    this.findApi.init(pdfFindController, eventBus);
    this.pdfPrint.init(this.pdfViewer, dispatchPrintEvent);
    this.enhancementManager.init(this.pdfViewer, dispatchEnhancementEvent);
  }

  updateEnhancementsContext(props) {
    this.enhancementManager.updateContextData(props);
  }

  async setDocument(pdfDocument) {
    this.pdfViewer.setDocument(pdfDocument);
    this.pdfLinkService.setDocument(pdfDocument, null);
    this.enhancementManager.updateDocument(pdfDocument);

    await setupIntegrations(pdfDocument, this.pdfViewer);

    pdfDidRender(this);
  }

  goToDestination(dest) {
    this.pdfLinkService.goToDestination(dest);
  }

  getPageWidth(pageIndex) {
    const pageView = this.pdfViewer.getPageView(pageIndex);
    const viewport = pageView.pdfPage.getViewport({ scale: 1 });
    const isPortrait = pageView.rotation % 180 === 0;

    return isPortrait ? viewport.width : viewport.height;
  }

  getCurrentPageWidth() {
    const pageIndex = this.pdfViewer.currentPageNumber - 1;
    return this.getPageWidth(pageIndex);
  }

  reRenderCurrent() {
    this.pdfViewer.renderingQueue.renderHighestPriority();
  }

  setPageRotation(pageIndex, degree) {
    const page = this.pdfViewer.getPageView(pageIndex);
    if (page && page.rotation !== degree) {
      page.update({ rotation: degree });
    }
    this.reRenderCurrent();
  }

  getMaxScale() {
    const pageIndex = this.pdfViewer.currentPageNumber - 1;
    const { width, height } = this.pdfViewer
      .getPageView(pageIndex)
      .pdfPage
      .getViewport({ scale: 1 });

    const maxCanvasPixels = this.pdfViewer.maxCanvasPixels
      ? this.pdfViewer.maxCanvasPixels
      : 4096 * 4096; // default maxCanvasPixels

    // If the devicePixelRatio is larger we get a smaller maxScale, the reason
    // for this is because when we have (x * y) pixels and display it on a ratio of 2
    // so we are actually getting (x * y * 2) pixels.
    // A devicePixelRatio of 1 (half of 2) can zoom twice as much, or,
    // a devicePixelRatio of 1 has twice the maxScale as a devicePixelRatio of 2.
    const currentPixels = (width * height * window.devicePixelRatio) / 2;

    const maxRatio = (maxCanvasPixels / currentPixels);
    return to4Decimal(maxRatio / 10);
  }

  /**
   * Converts scale from PDF (print) resolution (logical 72dpi) to screen resolution (logical 96dpi)
   * (i.e. multiplies by 96/72).
   *
   * @see {@link https://github.com/mozilla/pdf.js/issues/5628}
   */
  getPdfScale() {
    const viewerScale = this.pdfViewer.currentScale;
    const scale = viewerScale * CSS_UNITS;

    return to4Decimal(scale);
  }

  /**
   * Converts scale from screen resolution (logical 96dpi) to PDF (print) resolution (logical 72dpi)
   * (i.e. divides by 96/72).
   *
   * @see {@link https://github.com/mozilla/pdf.js/issues/5628}
   */
  setPdfScale(pdfScale) {
    const scale = pdfScale / CSS_UNITS;
    this.pdfViewer._setScale(to4Decimal(scale));
  }

  /**
   * Cancels the most recent print operation
   */
  cancelPrint() {
    this.pdfPrint.cancel();
  }
}

const instances = new Map();

/**
 * @param {string} [id=default]
 * @returns {PdfViewerApplication}
 */
function getPdfViewerApplication(id = 'default') {
  let viewer = instances.get(id);

  if (!viewer) {
    viewer = new PdfViewerApplication();
    instances.set(id, viewer);
  }

  return viewer;
}

export default getPdfViewerApplication;
